import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
    FaFacebookF, FaInstagram, FaWhatsapp, FaPhoneAlt, FaEnvelope, FaMapMarkerAlt
} from 'react-icons/fa';
import contactUsBg from '../assets/Mawathage photos/4.png';

const fadeUp = {
    hidden: { opacity: 0, y: 35 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: 'easeOut' } }
};

const scaleIn = {
    hidden: { opacity: 0, scale: 0.94 },
    visible: { opacity: 1, scale: 1, transition: { duration: 0.7, ease: 'easeOut' } }
};

const ContactUs = () => {
    const { t } = useTranslation('contact');

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        contactNumber: '',
        message: '',
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setSuccessMessage('');
        setErrorMessage('');

        const phoneNumber = '94742242794';
        const text = `${t('whatsappSubject') || 'New Contact from Website'}\n\n` +
            `${t('whatsappName') || 'Name'}: ${formData.name}\n` +
            `${t('whatsappEmail') || 'Email'}: ${formData.email}\n` +
            `${t('whatsappContactNumber') || 'Phone'}: ${formData.contactNumber || '-'}\n` +
            `${t('whatsappMessage') || 'Message'}:\n${formData.message}`;

        const formattedMessage = encodeURIComponent(text);
        const whatsappUrl = `https://wa.me/${phoneNumber}?text=${formattedMessage}`;

        try {
            window.open(whatsappUrl, '_blank');
            setTimeout(() => {
                setIsSubmitting(false);
                setSuccessMessage(t('successMessage') || 'Message sent! We will reply soon.');
                setFormData({ name: '', email: '', contactNumber: '', message: '' });
            }, 1200);
        } catch (err) {
            setIsSubmitting(false);
            setErrorMessage(t('errorMessage') || 'Something went wrong. Please try again.');
        }
    };

    return (
        <div className="min-h-screen bg-white text-[#173F2D]">

            {/* ─── HERO ──────────────────────────────────────────────── */}
            <header className="relative flex min-h-[460px] items-center justify-center overflow-hidden border-b border-[#C9A227]/40 bg-[#F7FAF8] sm:min-h-[720px]">
                <img
                    src={contactUsBg}
                    alt={t('heroAlt') || 'Mawathage Services contact page'}
                    className="absolute inset-0 h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-[#173F2D]/65" />
                <motion.div
                    initial="hidden"
                    animate="visible"
                    variants={fadeUp}
                    className="relative z-10 mx-auto w-full max-w-5xl px-5 py-20 text-center sm:px-8 md:py-28"
                >
                    <p className="mb-5 text-sm font-semibold uppercase tracking-[0.3em] text-[#F2D36B]">
                        {t('heroEyebrow')}
                    </p>
                    <h1 className="text-4xl font-serif font-light tracking-tight text-white sm:text-5xl md:text-7xl">
                        {t('heroTitle')}
                    </h1>

                    <p className="mx-auto mt-5 max-w-2xl text-lg leading-relaxed text-white/90 sm:text-xl">
                        {t('heroSubtitle')}
                    </p>
                </motion.div>
            </header>

            {/* ─── MAIN CONTENT ──────────────────────────────────────── */}
            <main className="relative z-10 pb-16 md:pb-24 pt-12 md:pt-16">
                <div className="container mx-auto px-5 sm:px-8 lg:px-12">

                    <div className="grid lg:grid-cols-2 gap-10 lg:gap-16">

                        {/* ─── LEFT ── Contact Info + Social ─────────────────────── */}
                        <motion.div
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true, margin: "-100px" }}
                            variants={fadeUp}
                            className="space-y-12 lg:space-y-16"
                        >
                            <div className="text-center lg:text-left">
                                <div className="inline-flex items-center gap-4 mb-5">
                                    <div className="h-px w-12 md:w-16 bg-amber-700/40 flex-1" />
                                    <h2 className="text-3xl sm:text-4xl md:text-5xl font-serif font-light text-[#173F2D] tracking-tight">
                                        {t('sectionTitle')}
                                    </h2>
                                    <div className="h-px w-12 md:w-16 bg-amber-700/40 flex-1" />
                                </div>

                                <h3 className="text-2xl sm:text-3xl md:text-4xl font-light text-[#2D3F3A] mt-6">
                                    {t('sectionSubtitle')}
                                </h3>

                                <p className="mt-5 max-w-2xl text-lg leading-relaxed text-[#46705A] sm:text-xl mx-auto lg:mx-0">
                                    {t('sectionDescription')}
                                </p>
                            </div>

                            {/* Contact Cards */}
                            <div className="grid sm:grid-cols-2 gap-6 lg:gap-8">

                                <motion.div
                                    variants={scaleIn}
                                    className="min-w-0 rounded-xl border border-[#DCE9E1] bg-white p-7 shadow-[0_10px_30px_rgba(23,63,45,0.08)] transition-all duration-300 hover:-translate-y-1 hover:border-[#C9A227]/60 hover:shadow-xl"
                                >
                                    <div className="flex items-center gap-4 mb-5">
                                        <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-[#EAF3ED] text-xl text-[#1F5D42]">
                                            <FaPhoneAlt />
                                        </div>
                                        <h4 className="text-xl md:text-2xl font-medium text-[#1A2F2A]">
                                            {t('callUs')}
                                        </h4>
                                    </div>
                                    <div className="space-y-2 text-lg text-[#1F5D42]">
                                        <p>+94 74 274 2794 <span className="text-sm text-[#6D7F74]">- Gayan Mawatage</span></p>
                                        <p>+94 76 223 6647 <span className="text-sm text-[#6D7F74]">- Carola</span></p>
                                    </div>
                                </motion.div>

                                <motion.div
                                    variants={scaleIn}
                                    className="rounded-xl border border-[#DCE9E1] bg-white p-7 shadow-[0_10px_30px_rgba(23,63,45,0.08)] transition-all duration-300 hover:-translate-y-1 hover:border-[#C9A227]/60 hover:shadow-xl"
                                >
                                    <div className="flex items-center gap-4 mb-5">
                                        <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-[#EAF3ED] text-xl text-[#1F5D42]">
                                            <FaEnvelope />
                                        </div>
                                        <h4 className="text-xl md:text-2xl font-medium text-[#1A2F2A]">
                                            {t('emailTitle')}
                                        </h4>
                                    </div>
                                    <div className="min-w-0 space-y-2 text-base text-[#1F5D42] sm:text-lg">
                                        <a href="mailto:info@mawatage-services.com" className="block max-w-full break-words transition-colors hover:text-[#B18A16]">info@mawatage-services.com</a>
                                        <a href="mailto:mawatageservices@gmail.com" className="block max-w-full break-words transition-colors hover:text-[#B18A16]">mawatageservices@gmail.com</a>
                                    </div>
                                </motion.div>

                                <motion.div
                                    variants={scaleIn}
                                    className="rounded-xl border border-[#DCE9E1] bg-white p-7 shadow-[0_10px_30px_rgba(23,63,45,0.08)] transition-all duration-300 hover:-translate-y-1 hover:border-[#C9A227]/60 hover:shadow-xl sm:col-span-2 lg:col-span-1"
                                >
                                    <div className="flex items-center gap-4 mb-5">
                                        <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-[#EAF3ED] text-xl text-[#1F5D42]">
                                            <FaMapMarkerAlt />
                                        </div>
                                        <h4 className="text-xl md:text-2xl font-medium text-[#1A2F2A]">
                                            {t('ourLocation')}
                                        </h4>
                                    </div>
                                    <p className="text-lg leading-relaxed text-[#1F5D42]">
                                        {t('locationAddress')}
                                    </p>
                                </motion.div>

                            </div>

                            {/* Social Icons */}
                            <div className="text-center lg:text-left">
                                <h4 className="mb-6 text-xl font-medium text-[#173F2D] md:text-2xl">
                                    {t('socialNetwork')}
                                </h4>
                                <div className="flex justify-center lg:justify-start gap-5">
                                    {[
                                        { href: "https://www.facebook.com/share/1BfGRTr59L/?mibextid=wwXIfr", icon: FaFacebookF, label: t('ariaFacebook') },
                                        { href: "https://www.instagram.com/mahibeach_ayurveda?igsh=bjExYzI0NWY4azNu", icon: FaInstagram, label: t('ariaInstagram') },
                                        { href: "https://wa.me/94742242794", icon: FaWhatsapp, label: t('ariaWhatsApp') },
                                    ].map((link, i) => (
                                        <motion.a
                                            key={i}
                                            href={link.href}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            aria-label={link.label}
                                            className="flex h-12 w-12 items-center justify-center rounded-full border border-[#C9A227]/50 bg-white text-[#C9A227] shadow-md transition-all duration-300 hover:bg-[#1F5D42] hover:shadow-lg text-xl"
                                            whileHover={{ scale: 1.12, y: -4 }}
                                        >
                                            <link.icon color="#C9A227" aria-hidden="true" />
                                        </motion.a>
                                    ))}
                                </div>
                            </div>
                        </motion.div>

                        {/* ─── RIGHT ── Contact Form ─────────────────────────────── */}
                        <motion.div
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true, margin: "-100px" }}
                            variants={scaleIn}
                            className="rounded-xl border border-[#DCE9E1] bg-white p-8 shadow-[0_18px_50px_rgba(23,63,45,0.12)] lg:p-10 xl:p-12"
                        >
                            <h2 className="mb-4 text-3xl font-serif font-light text-[#173F2D] md:text-4xl lg:text-5xl">
                                {t('getInTouch')}
                            </h2>
                            <p className="mb-10 text-lg text-[#46705A] md:text-xl">
                                {t('getInTouchDescription')}
                            </p>

                            <form onSubmit={handleSubmit} className="space-y-7">
                                {[
                                    { label: t('nameLabel'), name: 'name', type: 'text', required: true },
                                    { label: t('emailLabel'), name: 'email', type: 'email', required: true },
                                    { label: t('contactNumberLabel'), name: 'contactNumber', type: 'tel' },
                                    { label: t('messageLabel'), name: 'message', type: 'textarea', required: true },
                                ].map((field, i) => (
                                    <div key={i}>
                                        <label className="block text-lg font-medium text-[#2D3F3A] mb-2">
                                            {field.label}
                                        </label>
                                        {field.type === 'textarea' ? (
                                            <textarea
                                                name={field.name}
                                                value={formData[field.name]}
                                                onChange={handleChange}
                                                rows={5}
                                                required={field.required}
                                                className="
                          w-full rounded-lg border border-[#BFD7C8] bg-[#F7FAF8] px-5 py-4
                          text-lg text-[#173F2D] outline-none transition-all
                          placeholder:text-[#6D7F74] focus:border-[#C9A227] focus:bg-white resize-y min-h-[120px]
                        "
                                            />
                                        ) : (
                                            <input
                                                type={field.type}
                                                name={field.name}
                                                value={formData[field.name]}
                                                onChange={handleChange}
                                                required={field.required}
                                                className="
                          w-full rounded-lg border border-[#BFD7C8] bg-[#F7FAF8] px-5 py-4
                          text-lg text-[#173F2D] outline-none transition-all
                          placeholder:text-[#6D7F74] focus:border-[#C9A227] focus:bg-white
                        "
                                            />
                                        )}
                                    </div>
                                ))}

                                <div className="pt-4">
                                    {successMessage && (
                                        <p className="text-green-600 font-medium text-lg mb-4">{successMessage}</p>
                                    )}
                                    {errorMessage && (
                                        <p className="text-red-600 font-medium text-lg mb-4">{errorMessage}</p>
                                    )}

                                    <button
                                        type="submit"
                                        disabled={isSubmitting}
                                        className={`
                      w-full md:w-auto px-10 py-5 rounded-full
                      font-semibold text-lg md:text-xl
                      shadow-lg hover:shadow-xl transform hover:-translate-y-1
                      transition-all duration-300
                      ${isSubmitting
                                                ? 'bg-gray-400 cursor-not-allowed'
                                                : 'bg-[#1F5D42] text-white hover:bg-[#174A33]'
                                            }
                    `}
                                    >
                                        {isSubmitting ? t('sending') || 'Sending...' : t('sendMessage') || 'Send Message'}
                                    </button>
                                </div>
                            </form>
                        </motion.div>

                    </div>
                </div>
            </main>

            {/* ─── MAP ───────────────────────────────────────────────── */}
            <section className="border-t border-[#DCE9E1] bg-[#F7FAF8] py-16 md:py-24">
                <div className="container mx-auto px-5 sm:px-8 lg:px-12">
                    <motion.h2
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        variants={fadeUp}
                        className="mb-10 text-center text-3xl font-serif font-light text-[#173F2D] sm:text-4xl md:mb-16 md:text-5xl"
                    >
                        {t('findUsOnMap')}
                    </motion.h2>

                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        variants={scaleIn}
                        className="rounded-2xl md:rounded-3xl overflow-hidden shadow-2xl border border-teal-100/60"
                    >
                        <iframe
                            src="https://www.google.com/maps?q=No.61%2C%20Police%20Bungalow%20Road%2C%20Moragalla%2C%20Beruwala&output=embed"
                            width="100%"
                            height="500"
                            style={{ border: 0 }}
                            allowFullScreen=""
                            loading="lazy"
                            referrerPolicy="no-referrer-when-downgrade"
                            title={t('findUsOnMap')}
                        />
                    </motion.div>
                </div>
            </section>

        </div>
    );
};

export default ContactUs;